import React from 'react';
import { Copy, Trash2, Calendar, Users, Tag, FileText } from 'lucide-react';

/**
 * InviteCodeCard Component
 * Displays an individual invite code with all its details
 *
 * @param {Object} props - Component props
 * @param {Object} props.inviteCode - Invite code data object
 * @param {string} props.inviteCode._id - Unique identifier
 * @param {string} props.inviteCode.code - The actual invite code string
 * @param {string} props.inviteCode.userType - Type of user (referral, vendor, customer)
 * @param {number} props.inviteCode.currentUses - Number of times code has been used
 * @param {number} props.inviteCode.maxUses - Maximum allowed uses
 * @param {string} props.inviteCode.expiresAt - Expiration date
 * @param {string} props.inviteCode.createdAt - Creation date
 * @param {string} props.inviteCode.status - Status (ACTIVE, EXPIRED, etc.)
 * @param {Object} props.inviteCode.metadata - Additional metadata
 * @param {string} props.inviteCode.metadata.description - Code description
 * @param {string} props.inviteCode.metadata.campaign - Campaign name
 * @param {Function} props.onCopy - Callback when copy button is clicked
 * @param {Function} props.onDelete - Callback when delete button is clicked
 */
const InviteCodeCard = ({ inviteCode, onCopy, onDelete }) => {
  const isExpired = inviteCode.status === 'EXPIRED' || new Date(inviteCode.expiresAt) < new Date();
  const isFullyUsed = inviteCode.currentUses >= inviteCode.maxUses;
  const isActive = inviteCode.status === 'ACTIVE' && !isExpired && !isFullyUsed;

  return (
    <div className="border rounded-lg p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          {/* Code Display with Copy Button and Status Badge */}
          <div className="flex items-center space-x-3 mb-3">
            <code className="text-lg font-mono font-bold text-orange-600 bg-orange-50 px-3 py-1 rounded">
              {inviteCode.code}
            </code>
            <button
              onClick={() => onCopy(inviteCode.code)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" />
            </button>
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
              isActive ? 'bg-green-100 text-green-800' :
              isExpired ? 'bg-red-100 text-red-800' :
              isFullyUsed ? 'bg-gray-100 text-gray-800' :
              'bg-yellow-100 text-yellow-800'
            }`}>
              {isActive ? 'ACTIVE' : isExpired ? 'EXPIRED' : isFullyUsed ? 'FULLY USED' : inviteCode.status}
            </span>
          </div>

          {/* Code Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
            <div className="flex items-center">
              <Users className="w-4 h-4 mr-2 text-gray-400" />
              <div>
                <span className="font-medium">Uses:</span>
                <span className="ml-2">{inviteCode.currentUses}/{inviteCode.maxUses}</span>
              </div>
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-gray-400" />
              <div>
                <span className="font-medium">Expires:</span>
                <span className="ml-2">{new Date(inviteCode.expiresAt).toLocaleDateString()}</span>
              </div>
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-gray-400" />
              <div>
                <span className="font-medium">Created:</span>
                <span className="ml-2">{new Date(inviteCode.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
            <div className="flex items-center">
              <Tag className="w-4 h-4 mr-2 text-gray-400" />
              <div>
                <span className="font-medium">Type:</span>
                <span className="ml-2 capitalize">{inviteCode.userType}</span>
              </div>
            </div>
          </div>

          {/* Description */}
          {inviteCode.metadata?.description && (
            <div className="mt-3 flex items-start">
              <FileText className="w-4 h-4 mr-2 text-gray-400 mt-0.5" />
              <p className="text-sm text-gray-600">{inviteCode.metadata.description}</p>
            </div>
          )}

          {/* Campaign */}
          {inviteCode.metadata?.campaign && (
            <div className="mt-2 flex items-center">
              <Tag className="w-4 h-4 mr-2 text-gray-400" />
              <span className="text-xs text-gray-500">Campaign:</span>
              <span className="ml-2 text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded">
                {inviteCode.metadata.campaign}
              </span>
            </div>
          )}
        </div>

        {/* Delete Button */}
        {onDelete && (
          <div className="ml-4">
            <button
              onClick={() => onDelete(inviteCode._id)}
              className="text-red-600 hover:bg-red-50 p-2 rounded transition-colors"
              title="Delete invite code"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteCodeCard;
